import { useRef } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Avatar,
  Dropdown,
  DropdownContent,
  DropdownTextInput,
  Error,
  Tabs,
  Typography,
} from 'shared/ui'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { useElementWidth } from 'shared/hooks'
import type { IntegrationCrmUser } from 'entities/Integrations/model/IntegrationCrmUser'
import { minSearchCountText } from 'widgets/constants'
import { useCreateContext } from 'pages/clients/pages/create/context/createContext'
import styles from './styles.module.scss'

export const ClientInviteField = observer(() => {
  const { clientDetailsStore } = useCreateContext()
  const ref = useRef<HTMLLabelElement | null>(null)
  const triggerWidth = useElementWidth(ref) ?? 0
  const {
    inputValue,
    displayValue,
    isDropdownOpen,
    toggleDropdown,
    handleInput,
    selectUser,
    loading,
    canSearch,
    emailError,
    filteredUsers,
  } = clientDetailsStore.clientInviteFieldStore

  const showHubSpotTab = canSearch && (loading || filteredUsers.length > 0)
  const hubSpotTab = showHubSpotTab
    ? [{ key: 'hubspot', name: 'HubSpot', count: filteredUsers.length }]
    : []

  const renderDropdownContent = () => (
    <>
      {showHubSpotTab && (
        <Tabs
          tabs={hubSpotTab}
          activeTabKey='hubspot'
          handleSelectTab={() => {}}
          withStopPropagation
          tabsClassName={styles.tabs}
          tabClassName={styles.tab}
        />
      )}
      {!canSearch && (
        <div className={styles.dropdownMinChars}>
          <Typography variant='body-md-regular' color='tertiary' ariaLabel='ClientInviteMinChars'>
            {minSearchCountText}
          </Typography>
        </div>
      )}
      {canSearch && !loading && filteredUsers.length > 0 && (
        <div className={styles.dropdownOptions}>
          {filteredUsers.map((user: IntegrationCrmUser) => (
            <div key={user.id} className={styles.contactItem} onClick={() => selectUser(user)}>
              <div className={styles.dropdownLabel}>
                <Avatar info={user.avatarInfo} size={16} />
                <Typography
                  className={styles.dropdownName}
                  variant='text-input-field-r'
                  tooltipProps={{ label: user.name }}
                  ariaLabel='ClientInviteContactName'
                  ellipsis
                >
                  {user.name}
                </Typography>
              </div>
              {user.email && (
                <Typography
                  className={styles.dropdownEmail}
                  variant='body-xs-regular'
                  tooltipProps={{ label: user.email }}
                  ariaLabel='ClientInviteContactEmail'
                  color='tertiary'
                  ellipsis
                >
                  {user.email}
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}
      {canSearch && !loading && filteredUsers.length === 0 && (
        <div className={styles.dropdownEmpty}>
          <Typography variant='body-md-regular' color='tertiary' ariaLabel='ClientInviteNoResults'>
            No results found
          </Typography>
        </div>
      )}
    </>
  )

  return (
    <>
      <Dropdown
        ariaLabel='ClientInviteField'
        show={isDropdownOpen}
        onToggle={toggleDropdown}
        placement='bottom-start'
        triggerComponent={() => (
          <TextFieldPlaceholder
            ref={ref}
            variant='stroke'
            size='medium'
            error={!!emailError}
            leftActions={[
              makeTextFieldIcon({
                icon: 'search',
                color: 'var(--content-primary-tertiary)',
                fontSize: 16,
                className: styles.searchIcon,
              }),
            ]}
            placeholder={
              <Typography
                variant='text-input-field-r'
                color={
                  displayValue
                    ? 'var(--content-primary-primary)'
                    : 'var(--content-primary-disabled)'
                }
                ariaLabel='ClientInviteField_Placeholder'
                ellipsis
                tag='div'
              >
                {displayValue || 'Enter email address or name'}
              </Typography>
            }
          />
        )}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-4, -40],
              },
            },
          ],
        }}
        customComponent={() => (
          <DropdownContent noPadding>
            <DropdownTextInput
              textFieldProps={{
                value: inputValue,
                onChange: handleInput,
                InputProps: {
                  placeholder: 'Enter email address or name',
                },
                leftActions: [
                  makeTextFieldIcon({
                    icon: 'search',
                    className: styles.searchIcon,
                  }),
                ],
              }}
            />
            {renderDropdownContent()}
          </DropdownContent>
        )}
        width={triggerWidth + 8}
        margin={0}
      />
      {emailError && <Error type='text' error={emailError} />}
    </>
  )
})
